import type { Ritual } from '../data/ingredients';

/**
 * Stitch the seven ingredients into a poetic paragraph the user can read,
 * print, or memorize. Tries to read like a single instruction.
 */
export function composeRitual(r: Ritual): string {
  const sentence1 = `to begin: ${r.setting}, prepare ${r.thresholdObject}.`;
  const sentence2 = `say: "${r.openingWords}".`;
  const sentence3 = `${capitalize(r.posture)} for ${r.duration}.`;
  const sentence4 = `when it is time to close, ${r.closing}.`;
  return `${sentence1} ${sentence2} ${sentence3} ${sentence4}`;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function suggestedTitle(r: Ritual): string {
  // Generate a poetic title from the intent.
  switch (r.intent) {
    case 'devotion':
      return 'a small devotion';
    case 'gratitude':
      return 'a small gratitude';
    case 'a threshold to begin':
      return 'a threshold';
    case 'settling before sleep':
      return 'a settling';
    case 'preparation for a scene':
      return 'a preparation';
    case 'repair after difficulty':
      return 'a repair';
    case 'celebration of a milestone':
      return 'a marking';
    default:
      return `a ritual for ${r.intent}`;
  }
}

export function ritualToMarkdown(r: Ritual, composed: string): string {
  const title = r.title || suggestedTitle(r);
  return [
    `# ${title}`,
    '',
    `*for ${r.intent}*`,
    '',
    composed,
    '',
    `---`,
    `composed on ${new Date().toLocaleDateString()}`,
  ].join('\n');
}

export function ritualToText(r: Ritual, composed: string): string {
  const title = r.title || suggestedTitle(r);
  return [
    title.toUpperCase(),
    '',
    `for ${r.intent}`,
    '',
    composed,
    '',
    `composed on ${new Date().toLocaleDateString()}`,
  ].join('\n');
}

export function downloadFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
